import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { getQueryParamValue } from 'src/utils/app';
import colors from 'src/utils/colors';
import { useSnackbar } from 'src/utils/Hooks';
import { useBeaconClientId } from 'src/utils/Hooks/reduxSelectors';
import { GenerateContentForSkusRequest } from '../api/apiTypes';
import { useContentGenerationStore } from '../Generate/components/ProductTable/store';
import type { MergeQueryFn } from '../Generate/components/ProductTable/useProductTable';
import { CONTENT_GENERATION_QUERY_KEY, updateContentGenerationRecordInCache } from '../Generate/components/ProductTable/useProductTable';
import ProductTableV2 from '../Generate/components/ProductTableV2';
import { ContentGenerationRecord, GenerationStatusEnum } from '../Generate/types';
import { adaptInitialData, getInitialProductData } from '../Generate/utils';
import { GeneratingModal } from '../GeneratingModal';
import { useGenerateContentForSkus } from '../hooks/useContentGenerate';
import { useContentGenReviewStore } from '../stores';
import { useProjectFlowStore } from '../stores/projectFlowStore';
import { AddProductsModal } from './AddProductsModal';
import { useProjects, useProjectsSubscription } from './hooks';
import { useProjectWebSocket } from './hooks/useProjectWebSocket';
import ProjectFinalReview from './ProjectFinalReview';

export const ProjectReview: React.FC<{ isExisting?: boolean }> = ({ isExisting = false }) => {
  const { setContentGenItemsToReview } = useContentGenReviewStore();
  const {
    isFinalReview,
    setIsFinalReview,
    selectedRetailerSkus: persistedSelectedSkus,
    setSelectedRetailerSkus: setPersistedSelectedSkus,
    modalSelectedProducts: persistedModalSelectedProducts,
    addModalSelectedProducts,
    clearModalSelectedProducts
  } = useProjectFlowStore();
  const { selectedItems, setSelectedItems, isGeneratingModalOpen, openGeneratingModal, closeGeneratingModal } =
    useContentGenerationStore();

  const { showSnackbar } = useSnackbar();
  const beaconClientId = useBeaconClientId();
  const { generateContentForSkus, isPending: isGenerating } = useGenerateContentForSkus();
  const [isAddProductsModalOpen, setIsAddProductsModalOpen] = useState(false);
  const [allProjectProducts, setAllProjectProducts] = useState<ContentGenerationRecord[]>([]);
  // Track the projectId for the modal separately to handle new project creation
  // where the store update hasn't triggered a re-render yet
  const [modalProjectId, setModalProjectId] = useState<string | undefined>(undefined);
  // Derive Set from persisted array for use in component
  const modalSelectedProducts = useMemo(
    () => new Set(persistedModalSelectedProducts),
    [persistedModalSelectedProducts]
  );
  const queryClient = useQueryClient();
  const allProjectProductsKeyRef = useRef<string>('');
  const hasRestoredSelectionsRef = useRef(false);
  const isAwaitingFirstWebSocketEventRef = useRef(false);
  // Track the projectId to clear modalSelectedProducts when switching projects
  const previousProjectIdRef = useRef<string | null | undefined>(undefined);

  const {
    projectId,
    projectName,
    profileId,
    profileName,
    profileVersion,
    navigateToProjectProducts,
    navigateToProjectSettings,
    setProjectDetails,
    createProjectMutation,
    beaconNavigator
  } = useProjects({
    syncUrlParams: true,
    requireProjectId: isExisting // Only require projectId for existing projects
  });

  // Restore persisted selections on mount (only for new project creation flow)
  useEffect(() => {
    if (!isExisting && persistedSelectedSkus.length > 0 && !hasRestoredSelectionsRef.current) {
      setSelectedItems(new Set(persistedSelectedSkus));
      hasRestoredSelectionsRef.current = true;
    }
  }, [isExisting, persistedSelectedSkus, setSelectedItems]);

  // Sync selections to persisted store (only for new project creation flow, before project is created)
  // Once projectId exists, we fetch by projectId instead of relying on persisted selections
  useEffect(() => {
    if (!isExisting && !projectId) {
      const skusArray = Array.from(selectedItems);
      setPersistedSelectedSkus(skusArray);
    }
  }, [selectedItems, isExisting, projectId, setPersistedSelectedSkus]);

  // Clear modalSelectedProducts when switching to a different project
  useEffect(() => {
    if (isExisting && projectId) {
      // On first mount, just set the ref without clearing
      if (previousProjectIdRef.current === undefined) {
        previousProjectIdRef.current = projectId;
      } else if (previousProjectIdRef.current !== projectId) {
        // projectId changed - clear the modal selections from the previous project
        clearModalSelectedProducts();
        previousProjectIdRef.current = projectId;
      }
    }
  }, [isExisting, projectId, clearModalSelectedProducts]);

  // Sync profile details from existing project products (only for existing projects)
  // For new projects, we preserve the profile selected in ProjectSettings
  useEffect(() => {
    if (!isExisting) return;

    const firstProduct = allProjectProducts?.[0];
    const profileId = firstProduct?.profileId;
    const profileVersion = firstProduct?.profileVersion;

    // Only update if we have valid profile info from the products
    if (profileId && typeof profileVersion === 'number' && profileVersion > 0) {
      setProjectDetails({
        profileId,
        profileVersion
      });
    }
  }, [allProjectProducts, setProjectDetails, isExisting]);

  // Derive all project products from react-query cache without new props
  useEffect(() => {
    const computeAllProducts = () => {
      const queries = queryClient.getQueriesData<{
        pages: { records: ContentGenerationRecord[]; totalCount: number }[];
        pageParams: unknown[];
      }>({ queryKey: [CONTENT_GENERATION_QUERY_KEY] });

      // Filter queries to only include those matching the current project context
      // Query key structure: [CONTENT_GENERATION_QUERY_KEY, variant, appFilters, searchQuery, retailerSkus, projectId]

      const relevantQueries = queries.filter(([key]) => {
        if (!Array.isArray(key)) return false;
        const queryVariant = key?.[1];
        const queryProjectId = key?.[5];
        // Exclude merge queries
        if (queryVariant === 'merge') return false;
        // For existing projects, only include queries with matching projectId
        if (projectId) {
          return queryProjectId === projectId;
        }
        // For new projects (no projectId), include queries with variant 'review' or no projectId
        return queryVariant === 'review' || !queryProjectId;
      });

      const skuToRecord = new Map<string, ContentGenerationRecord>();
      relevantQueries.forEach(([, data]) => {
        data?.pages?.forEach((page) => {
          page.records.forEach((rec) => {
            skuToRecord.set(rec.retailerSku, rec);
          });
        });
      });

      const newKey = JSON.stringify(Array.from(skuToRecord.keys()).sort());
      if (newKey !== allProjectProductsKeyRef.current) {
        allProjectProductsKeyRef.current = newKey;
        setAllProjectProducts(Array.from(skuToRecord.values()));
      }
    };

    computeAllProducts();
    const unsubscribe = queryClient.getQueryCache().subscribe(computeAllProducts);
    return () => unsubscribe();
  }, [queryClient, isExisting, projectId]);

  const selectedRetailerSkus = useMemo(() => {
    return Array.from(selectedItems);
  }, [selectedItems]);

  // Subscribe to project events (invalidate projects list when projects change)
  useProjectsSubscription();

  // Subscribe to websocket events for project status updates
  const handleWebSocketStatusUpdate = useCallback(
    (update: { projectId?: string }) => {
      if (isAwaitingFirstWebSocketEventRef.current && update.projectId === projectId) {
        isAwaitingFirstWebSocketEventRef.current = false;
        // Modal is already open via local state (setIsGeneratingModalOpen in handleGenerate)
      }
    },
    [projectId]
  );

  useProjectWebSocket({
    projectId,
    retailerSkus: selectedRetailerSkus,
    enabled: !!projectId,
    onStatusUpdate: handleWebSocketStatusUpdate
  });

  // Get all retailer SKUs from the project (used to exclude in modal)
  const allProjectRetailerSkus = useMemo(() => {
    return allProjectProducts.map((product) => product.retailerSku);
  }, [allProjectProducts]);

  useEffect(() => {
    return () => {
      setIsFinalReview(false);
      setContentGenItemsToReview([]);
      // Don't clear selection here - it should persist when navigating back during project creation
      // Selection is cleared when starting a new project via AddProjectButton
    };
  }, [setIsFinalReview, setContentGenItemsToReview]);

  // If opened via deep link (new tab), initialize Final Review state from query
  useEffect(() => {
    const state = getQueryParamValue('finalReviewState');
    if (!state) return;
    // Filter out items that are still generating or have generation errors
    const itemsForReview = allProjectProducts.filter(
      (p) =>
        !!p.skuContentGenerationId &&
        p.status !== GenerationStatusEnum.enum.GENERATING &&
        p.status !== GenerationStatusEnum.enum.GENERATION_ERROR
    );
    if (itemsForReview.length) {
      // Sort items so the URL SKU is always first
      const urlSku = getQueryParamValue('sku');
      const urlContentGenId = getQueryParamValue('contentGenId');
      const sortedItems = [...itemsForReview].sort((a, b) => {
        const aIsUrlItem = a.retailerSku === urlSku || a.skuContentGenerationId === urlContentGenId;
        const bIsUrlItem = b.retailerSku === urlSku || b.skuContentGenerationId === urlContentGenId;
        if (aIsUrlItem && !bIsUrlItem) return -1;
        if (!aIsUrlItem && bIsUrlItem) return 1;
        return 0;
      });
      setContentGenItemsToReview(sortedItems);
      setIsFinalReview(true);
    }
  }, [allProjectProducts, setContentGenItemsToReview, setIsFinalReview]);

  const handleBack = () => {
    if (isExisting) {
      navigateToProjectSettings('edit');
    } else {
      navigateToProjectProducts();
    }
  };

  const handleGenerate = useCallback(async () => {
    const retailerId = parseInt(getQueryParamValue('rid'));

    if (!retailerId) {
      showSnackbar({
        type: 'error',
        message: 'Missing retailer ID.'
      });
      return;
    }

    if (selectedRetailerSkus.length === 0) {
      showSnackbar({
        type: 'error',
        message: 'No products selected for generation.'
      });
      return;
    }

    if (!profileId) {
      showSnackbar({
        type: 'error',
        message: 'No profile selected.'
      });
      return;
    }

    let effectiveProjectId = projectId;

    // If this is a new project (no projectId), create it first
    if (!isExisting && !projectId) {
      if (!projectName) {
        showSnackbar({
          type: 'error',
          message: 'Project name is required.'
        });
        return;
      }

      if (!profileName) {
        showSnackbar({
          type: 'error',
          message: 'Profile name is required.'
        });
        return;
      }

      try {
        const createResponse = await createProjectMutation.mutateAsync({
          name: projectName,
          profileId,
          profileVersion,
          profileName
        });

        // Extract projectId from response
        if (createResponse?.data?.projectId) {
          effectiveProjectId = createResponse.data.projectId;

          // Update store with the new projectId
          setProjectDetails({
            projectId: effectiveProjectId
          });

          // Set modal projectId immediately so it's available when the modal opens
          setModalProjectId(effectiveProjectId);
       } else {
          showSnackbar({
            type: 'error',
            message: 'Failed to create project - no project ID returned.'
          });
          return;
        }
      } catch (error) {
        console.error('Failed to create project:', error);
        // Error is already handled by the mutation's onError
        return;
      }
    }

    // Create content requests for all selected SKUs
    const contentRequests = selectedRetailerSkus.map((retailerSku) => ({
      retailerSku,
      profileId,
      profileVersion
    }));

    const requestBody: GenerateContentForSkusRequest = {
      beaconClientId,
      retailerId,
      contentRequests,
      ...(effectiveProjectId !== undefined && effectiveProjectId !== null && { projectId: effectiveProjectId })
    };

    // Build a map of retailerSku to current version for optimistic increment
    const skuToCurrentVersion = new Map<string, number>();
    allProjectProducts.forEach((product) => {
      skuToCurrentVersion.set(product.retailerSku, product.version || 0);
    });

    // Optimistically update the status of selected items to GENERATING
    selectedRetailerSkus.forEach((retailerSku) => {
      const currentVersion = skuToCurrentVersion.get(retailerSku) || 0;
      updateContentGenerationRecordInCache(queryClient, {
        retailerSku,
        status: GenerationStatusEnum.enum.GENERATING,
        beaconClientId: String(beaconClientId),
        skuContentGenerationId: '',
        profileName: profileName || '',
        profileId: profileId || '',
        profileVersion,
        version: currentVersion + 1,
        updatedAt: new Date().toISOString(),
        // Include projectId so the GeneratingModal can track progress immediately
        ...(effectiveProjectId && { projectId: effectiveProjectId })
      });
    });
    openGeneratingModal();

    generateContentForSkus(requestBody, {
      onSuccess: () => {
        // Only clear modalSelectedProducts for new projects
        // For existing projects, keep them so the merge query continues to show the products
        // until they appear in the main project query with their new status
        if (!isExisting) {
          clearModalSelectedProducts();
        }
        setSelectedItems(new Set());

        // Set flag to open modal on first websocket event
        isAwaitingFirstWebSocketEventRef.current = true;
      },
      onError: (error) => {
        console.error('Failed to generate content', error);
        showSnackbar({
          type: 'error',
          message: 'Failed to generate content.'
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    beaconClientId,
    profileId,
    profileName,
    profileVersion,
    selectedRetailerSkus,
    generateContentForSkus,
    showSnackbar,
    projectId,
    projectName,
    isExisting,
    createProjectMutation,
    setProjectDetails,
    queryClient,
    beaconNavigator
  ]);

  const mergeQueryFn: MergeQueryFn = useCallback(
    async ({ pageParam = 1, appFilters, variant, pageSize, searchTerm }) => {
      if (isExisting && !modalSelectedProducts.size) {
        return {
          records: [],
          totalCount: 0
        };
      }

      const retailerSkusToFetch = isExisting ? Array.from(modalSelectedProducts) : selectedRetailerSkus;

      if (retailerSkusToFetch.length === 0) {
        return {
          records: [],
          totalCount: 0
        };
      }

      const data = await getInitialProductData({
        variant,
        appFilters,
        pageNumber: pageParam,
        pageSize,
        searchTerm,
        projectId: undefined,
        retailerSkus: retailerSkusToFetch,
        overrideStatuses: [
          GenerationStatusEnum.enum.READY_TO_REVIEW,
          GenerationStatusEnum.enum.GENERATING,
          GenerationStatusEnum.enum.REVIEWED,
          GenerationStatusEnum.enum.GENERATION_ERROR,
          GenerationStatusEnum.enum.NO_GENERATION
        ]
      });
      const adapted = adaptInitialData(data.records);

      const selectedSet = new Set<string>([...retailerSkusToFetch, ...selectedRetailerSkus]);

      // If there's no projectId, cast all statuses to READY_TO_GENERATE
      // Otherwise, only coerce selected items
      const coerced = adapted.map((rec) => {
        if (!projectId) {
          return { ...rec, status: GenerationStatusEnum.enum.READY_TO_GENERATE, profileId: profileId };
        }
        return selectedSet.has(rec.retailerSku)
          ? { ...rec, status: GenerationStatusEnum.enum.READY_TO_GENERATE, profileId: profileId }
          : rec;
      });

      return {
        records: coerced,
        totalCount: data.totalCount
      };
    },
    [modalSelectedProducts, isExisting, selectedRetailerSkus, profileId, projectId]
  );

  const modalSelectedProductsArray = useMemo(() => Array.from(modalSelectedProducts), [modalSelectedProducts]);

  const handleGeneratingModalClose = useCallback(() => {
    setModalProjectId(undefined);
    closeGeneratingModal();
  }, [closeGeneratingModal]);

  const handleGeneratingModalBack = useCallback(() => {
    setModalProjectId(undefined);
    closeGeneratingModal();
  }, [closeGeneratingModal]);

  return (
    <BasePageContainer>
      {isFinalReview ? (
        <ProjectFinalReview />
      ) : (
        <>
          <Flex flexDirection="column">
            <Flex flexDirection="column" gap={8} marginBottom="32px">
              <Text variant="h4">Products</Text>
              <Text variant="body1">
                Select one or more products from your project and click generate to create new content.
              </Text>
            </Flex>
            <Box width="100%">
              <ProductTableV2
                // Use 'edit' variant when projectId exists to exclude NO_GENERATION status
                variant={isExisting ? 'edit' : 'review'}
                projectId={projectId}
                // Only use retailerSkus when there's no projectId yet (before project creation)
                // After project creation or on refresh with projectId, fetch based on projectId
                retailerSkus={!isExisting && !projectId ? [...selectedRetailerSkus] : undefined}
                mergeQueryFn={mergeQueryFn}
                mergeQueryOverrides={{ retailerSkus: modalSelectedProductsArray }}
                overrideStatus={!projectId ? 'READY_TO_GENERATE' : undefined}
                onEnableFinalReview={() => {
                  setIsFinalReview(true);
                }}
                // ProjectReview renders its own GeneratingModal, so hide the one in ProductTableV2
                hideGeneratingModal
              />
            </Box>

            <Flex justifyContent="space-between" sx={{ marginTop: '64px' }}>
              <SlButton
                onClick={handleBack}
                buttonSx={{
                  border: `1px solid ${colors.graysSecondary}`
                }}
              >
                Back
              </SlButton>

              <Flex gap={16}>
                {isExisting && (
                  <SlButton
                    onClick={() => setIsAddProductsModalOpen(true)}
                    buttonSx={{
                      border: `1px solid ${colors.graysSecondary}`,
                      backgroundColor: '#F6F7F8'
                    }}
                  >
                    + Add Products
                  </SlButton>
                )}
                <SlButton
                  variant="contained"
                  onClick={handleGenerate}
                  disabled={selectedRetailerSkus.length === 0 || isGenerating}
                >
                  {isGenerating ? 'Generating...' : 'Generate'}
                </SlButton>
              </Flex>
            </Flex>
          </Flex>

          {isExisting && (
            <AddProductsModal
              open={isAddProductsModalOpen}
              onClose={() => setIsAddProductsModalOpen(false)}
              excludeRetailerSkus={allProjectRetailerSkus}
              projectId={projectId}
              variant="edit"
              // Extend existing modal selections instead of replacing them
              setModalSelectedProducts={(newProducts) => {
                addModalSelectedProducts(Array.from(newProducts));
              }}
            />
          )}
        </>
      )}

      <GeneratingModal
        open={isGeneratingModalOpen}
        onClose={handleGeneratingModalClose}
        onBack={handleGeneratingModalBack}
        projectId={modalProjectId ?? projectId}
      />
    </BasePageContainer>
  );
};
